import { useState } from 'react';
import { FiCheck, FiCopy, FiLink } from 'react-icons/fi';

import { DateTimePicker } from '@/components/DateTimePicker';
import { inviteExpiryError } from '@/store/invites/invites.mappers';
import { useGenerateInviteLink } from '@/store/invites/invites.store';
import { toast } from '@/utils/toast';

import s from './InviteLinkButton.module.scss';

const COPIED_MS = 2000;

interface InviteLinkButtonProps {
  eventId: string;
  /** The event's end: the latest expiry the link may be given. */
  eventEndsAt?: string;
}

export function InviteLinkButton({ eventId, eventEndsAt }: InviteLinkButtonProps) {
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const { mutateAsync, isPending } = useGenerateInviteLink();

  // Asking again for a live link only retimes it: the URL already shared keeps working.
  const generate = async (expiry = expiresAt) => {
    try {
      const invite = await mutateAsync({
        event: { id: eventId, expires_at: expiry ?? undefined },
        service_type: 'vessel',
      });
      if (!invite?.link) {
        toast.error('Couldn’t generate the invite link. Please try again.');
        return;
      }
      setLink(invite.link);
    } catch {
      toast.error('Couldn’t generate the invite link. Please try again.');
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      toast.error('Couldn’t copy the link. Please try again.');
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), COPIED_MS);
  };

  const expiryError = inviteExpiryError(expiresAt, eventEndsAt);

  const pickExpiry = (value: string) => {
    setExpiresAt(value);
    if (link && !inviteExpiryError(value, eventEndsAt)) void generate(value);
  };

  return (
    <>
      <DateTimePicker
        label="Link Expires"
        placeholder="When the event starts"
        value={expiresAt}
        onChange={pickExpiry}
        minDate={new Date()}
        maxDate={eventEndsAt ? new Date(eventEndsAt) : undefined}
        showTimeInput
        error={expiryError}
      />
      {link ? (
        <button type="button" className={s.inviteLinkButton} onClick={copy}>
          <span className={s.inviteLinkIcon}>{copied ? <FiCheck /> : <FiCopy />}</span>
          {copied ? 'Link Copied' : 'Copy Link'}
        </button>
      ) : (
        <button
          type="button"
          className={s.inviteLinkButton}
          onClick={() => void generate()}
          disabled={isPending || !!expiryError}
        >
          <span className={s.inviteLinkIcon}>
            <FiLink />
          </span>
          {isPending ? 'Generating…' : 'Generate Invite Link'}
        </button>
      )}
    </>
  );
}
